import {Dialog, Typography, Box, Button, DialogTitle, DialogContent} from '@mui/material'
import {styles} from './styles'
import {JobSiteCreationForm} from './JobSiteCreationForm'
import {useCallback, useState} from 'react'
import {JobSiteCreationMap} from './JobSiteCreationMap'
import {JobSiteCreationLoading} from './JobSiteCreationLoading'
import {useCreateShipToMutation} from './hooks/useCreateShipToMutation'
import {useShipToCreationContext} from '@hconnect/tuc-processes/shipToCreation/context/shipToCreationContext'
import {t} from 'i18next'
import {ReactComponent as CancelIcon} from './assets/cancel.svg'

export const JobSiteCreationModal: React.FC<{open: boolean; onClose: () => void}> = ({
  open,
  onClose
}) => {
  const {
    processData,
    shipToCreationForm: {getValues}
  } = useShipToCreationContext()
  const [isMapOpened, setIsMapOpened] = useState(true)
  const [showForm, setShowForm] = useState(!isMapOpened)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isError, setIsError] = useState(false)

  const getSiteRequestPayload = () => {
    const formData = getValues()
    const {jobSiteAddress} = formData

    const {orgUnitId, customerId} = processData
    const payload = {
      orgUnitId,
      customerId,
      countryId: jobSiteAddress!.country,
      businessLine: 'RMC',
      siteName: formData.jobSiteName,
      street: jobSiteAddress!.street,
      houseNo: jobSiteAddress!.streetNumber,
      postalCode: jobSiteAddress!.postalCode,
      city: jobSiteAddress!.city,
      lat: jobSiteAddress!.latitude.toString(),
      lng: jobSiteAddress!.longitude.toString(),
      state: jobSiteAddress!.region,
      siteContactName: formData.siteContact?.name,
      ...(formData.siteContact?.phoneNumber?.dialingCode &&
        formData.siteContact?.phoneNumber?.number && {
          siteContactPhone:
            formData.siteContact.phoneNumber.dialingCode + formData.siteContact.phoneNumber.number
        })
    }
    return payload
  }

  const shipToCreationMutation = useCreateShipToMutation()

  const handleCreateShipTo = () => {
    setIsLoading(true)

    shipToCreationMutation.mutate(getSiteRequestPayload(), {
      onSuccess: () => {
        setIsLoading(false)
        setIsSuccess(true)
        setIsMapOpened(false)
        setShowForm(false)
      },
      onError: (error) => {
        setIsLoading(false)
        setIsError(true)
        setIsSuccess(false)
        setIsMapOpened(false)
        setShowForm(false)
      }
    })
  }

  const openMap = () => {
    setIsMapOpened(true)
    setShowForm(false)
  }

  const closeForm = () => {
    setShowForm(false)
    setIsMapOpened(true)
  }

  const onConfirm = () => {
    setIsMapOpened(false)
    setShowForm(true)
  }

  const onCloseModal = useCallback(() => {
    if (showForm || isError) {
      setShowForm(isError)
      setIsError(false)
      setIsMapOpened(!isError)
    } else {
      onClose()
    }
  }, [showForm, isError, onClose])

  return (
    <Dialog
      open={open}
      onClose={onCloseModal}
      maxWidth={isMapOpened ? 'lg' : 'sm'}
      fullWidth
      data-test-id="create-jobSite-modal"
      PaperProps={{
        sx: {
          ...styles.dialogPaper,
          ...(isMapOpened && {
            height: '80vh',
            maxHeight: '80vh'
          })
        }
      }}
    >
      {isError && (
        <Box sx={{p: 3}}>
          <DialogTitle sx={{p: 0, mb: 3}} data-test-id="error-dialog-title">
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexDirection: 'row'
              }}
            >
              <CancelIcon style={{width: 24, height: 24}} />
              <Typography
                variant="h3"
                component="div"
                sx={{...styles.titleText, flex: 1}}
                data-test-id="error-dialog-title-text"
              >
                {t('orderIntake.rmc.jobSiteModal.error.title')}
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent data-test-id="error-dialog-content" sx={{p: 0}}>
            <Box sx={styles.errorContainer} data-test-id="error-container">
              <Typography sx={styles.errorMessage} data-test-id="error-message-1">
                {t('orderIntake.rmc.jobSiteModal.error.message')}
              </Typography>

              <Button
                variant="outlined"
                onClick={onClose}
                fullWidth
                sx={styles.errorButton}
                data-test-id="back-to-jobsites-button"
              >
                {t('orderIntake.rmc.jobSiteModal.error.backToJobSitesButton')}
              </Button>
            </Box>
          </DialogContent>
        </Box>
      )}
      {isSuccess && shipToCreationMutation.data?.data.siteId && (
        <JobSiteCreationLoading
          onClose={onClose}
          siteId={shipToCreationMutation.data?.data.siteId}
        />
      )}
      {isMapOpened && <JobSiteCreationMap onClose={onClose} onConfirm={onConfirm} />}
      {showForm && (
        <JobSiteCreationForm
          onClose={closeForm}
          openMap={openMap}
          submit={handleCreateShipTo}
          isLoading={isLoading}
        />
      )}
    </Dialog>
  )
}
